import axios from "axios";

import {
  getCognitoUsername,
  getSecureCookie,
  setSecureCookie,
  removeSecureCookie,
} from "@/utils/authUtils";
import { getLocalizedErrorMessage } from "@/utils/errorMessageMapper";
/* eslint-disable @typescript-eslint/no-explicit-any */

const ipponApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
});

// リフレッシュ中フラグ（重複リクエストを防ぐ）
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

// キューの処理
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

// 最終的なログアウト処理
function forceLogout() {
  // トークンをクリア
  removeSecureCookie("token");
  removeSecureCookie("refreshToken");
  removeSecureCookie("my_use_service");

  // ローカルストレージとセッションストレージもクリア
  localStorage.clear();
  sessionStorage.clear();

  // Reduxストアもクリアするためにイベントを発火
  window.dispatchEvent(new CustomEvent("auth-logout"));

  // ログイン画面に強制遷移（現在のURLを保存）
  const currentPath = window.location.pathname + window.location.search;
  sessionStorage.setItem("redirectPath", currentPath);

  // referrer_url Cookieをチェックしてリダイレクト先を決定
  const referrerUrl = getSecureCookie("referrer_url");

  if (referrerUrl) {
    // referrer_urlがある場合はそのURLへリダイレクト
    window.location.href = referrerUrl;
  } else {
    // referrer_urlがない場合はログイン画面にリダイレクト
    window.location.href = "/404";
  }
}

// JWTトークンの有効期限をチェック（バッファー時間: 30秒）
const isTokenExpiringSoon = (token: string): boolean => {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return true;

    const payload = parts[1];
    // パディングを追加
    const paddedPayload = payload + "=".repeat((4 - (payload.length % 4)) % 4);
    // URLセーフ文字を標準Base64文字に変換
    const base64 = paddedPayload.replace(/-/g, "+").replace(/_/g, "/");

    const decodedPayload = JSON.parse(atob(base64));
    const exp = decodedPayload.exp;

    if (!exp) return true;

    const currentTime = Math.floor(Date.now() / 1000);
    const bufferTime = 30; // 30秒のバッファー

    return exp <= currentTime + bufferTime;
  } catch (error) {
    console.error("Error checking token expiration:", error);
    return true; // エラーの場合は期限切れとして扱う
  }
};

// リフレッシュトークンを使ってトークンを更新
const refreshAuthToken = async (): Promise<string> => {
  const refreshToken = getSecureCookie("refreshToken");
  const username = getCognitoUsername();

  if (!refreshToken) {
    console.error("Refresh token not found in cookies");
    throw new Error("Refresh token not found");
  }

  if (!username) {
    console.error("Username not found in JWT token");
    throw new Error("Username not found");
  }

  try {
    // 循環参照を避けるため、新しいaxiosインスタンスを作成
    const refreshApi = axios.create({
      baseURL: import.meta.env.VITE_API_URL,
    });

    console.log("Attempting token refresh...");
    const response = await refreshApi.post("/api/v1/auth/refresh-token", {
      refreshToken: refreshToken,
      username: username,
    });

    const { token } = response.data.data;

    if (!token || !token.IdToken) {
      console.error("Invalid token response:", response.data);
      throw new Error("Invalid token response from server");
    }

    // 新しいトークンをCookieに保存
    setSecureCookie("token", token.IdToken, 1);

    // 新しいRefreshTokenも保存（返却された場合）
    if (token.RefreshToken) {
      setSecureCookie("refreshToken", token.RefreshToken, 7);
      console.log("Token refresh successful with new refresh token");
    } else {
      console.log("Token refresh successful (refresh token not updated)");
    }

    return token.IdToken;
  } catch (error: any) {
    console.error("Token refresh failed:", {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      data: error.response?.data,
    });
    throw error;
  }
};

ipponApi.interceptors.request.use(
  async (config) => {
    // リフレッシュトークンエンドポイントの場合はトークンチェックをスキップ
    if (config.url?.includes("/auth/refresh-token")) {
      return config;
    }

    let token = getSecureCookie("token");

    if (token) {
      // トークンの有効期限をチェック
      if (isTokenExpiringSoon(token)) {
        // リフレッシュトークンが存在するかチェック
        const refreshToken = getSecureCookie("refreshToken");

        if (!refreshToken) {
          // リフレッシュトークンがない場合はログアウト
          forceLogout();
          return Promise.reject(new Error("No refresh token available"));
        }

        // 既にリフレッシュ中の場合はキューに追加
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve: (newToken) => {
                if (newToken) {
                  config.headers.Authorization = `Bearer ${newToken}`;
                }
                resolve(config);
              },
              reject,
            });
          });
        }

        // リフレッシュトークン処理を実行
        isRefreshing = true;

        try {
          const newToken = await refreshAuthToken();

          // キューを処理
          processQueue(null, newToken);

          // 新しいトークンを使用
          token = newToken;
        } catch (refreshError) {
          // リフレッシュ失敗時はログアウト
          processQueue(refreshError, null);
          forceLogout();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

ipponApi.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // 契約満了 (CONTRACT_EXPIRED) は契約切れによる強制ログアウト。理由を保存して
    // ログイン画面で「契約期間が終了しています」を表示できるようにする（SPEC §2-3）。
    // 管理画面化（SPEC §7.4）：契約前 (CONTRACT_NOT_STARTED) も同じ扱いにする。
    const accessCode = error.response?.data?.code;
    if (
      error.response?.status === 401 &&
      (accessCode === "CONTRACT_EXPIRED" || accessCode === "CONTRACT_NOT_STARTED")
    ) {
      sessionStorage.setItem("logoutReason", accessCode);
      forceLogout();
      return Promise.reject(error);
    }

    // 401エラーの場合、プロアクティブ処理でカバーできなかったケースなので
    // 直接ログアウト処理を実行（セキュリティ重視）。
    // ただしログイン(signin)自体の401は「認証失敗」であり、セッション失効ではない。
    // forceLogout(=強制リダイレクト)するとログイン画面のエラーメッセージが消えてしまうため、
    // signin リクエストは除外し、ログイン画面側でエラー表示させる。
    const requestUrl = error.config?.url ?? "";
    const isSignInRequest = requestUrl.includes("/auth/signin");
    if (error.response?.status === 401 && !isSignInRequest) {
      console.warn(
        "401 Unauthorized - プロアクティブリフレッシュでカバーできませんでした"
      );
      forceLogout();
      return Promise.reject(error);
    }

    // エラーレスポンスのメッセージを日本語化
    if (error.response?.data) {
      const localizedMessage = getLocalizedErrorMessage(error);
      error.response.data = {
        ...error.response.data,
        message: localizedMessage,
        status: error.response.status,
      };
    }

    return Promise.reject(error);
  }
);
/* eslint-enable @typescript-eslint/no-explicit-any */
export default ipponApi;
